import * as R from 'ramda';
import React, { memo, useMemo, useCallback } from 'react';
// components
import { PageFilterItem } from '../filter/report-and-quick-filters';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// icons
import * as I from '../../svgs';
// ui
import { Box, Flex } from '../../ui';
// component edit-report
import {
  isNotReportValid,
  mapFieldsWithSequenceFromIndex,
} from '../edit-report/helpers';
// component report-filters
import ReportSortItem from './sort-item';
import ReportFiltersEditor from './report-filters-editor';
import {
  getWithoutOrGroup,
  getReportFilterName,
  getReportFilterGroups,
  getFilterPopperSettings,
  getReportFiltersAvailableFields,
} from './helpers';
//////////////////////////////////////////////////

const EditButton = ({ handleClick }) => (
  <Flex
    mr={12}
    mb={10}
    cursor='pointer'
    onClick={handleClick}
    title={G.getWindowLocale('actions:edit', 'Edit')}
  >
    {I.pencil(G.getTheme('colors.dark.blue'), 14, 14)}
  </Flex>
);

const ReportFilterItems = ({
  sort,
  filters,
  reportType,
  handleChange,
  availableFields,
}) => {
  const availableFieldsByValue = useMemo(
    () => R.indexBy(R.prop(GC.FIELD_VALUE), availableFields),
    [availableFields],
  );

  const selectedReport = useMemo(() => ({
    searchCriteria: filters,
    [GC.FIELD_TYPE]: reportType,
  }), [filters, reportType]);

  const groupedFilters = useMemo(
    () => R.groupBy(R.prop('displayGroupId'), getReportFilterGroups(filters)),
    [filters],
  );

  const handleSetUsedReport = useCallback((report) => {
    if (isNotReportValid(report)) return;

    const nextFilters = R.filter(
      G.isNotNil,
      R.pathOr([], ['searchCriteria'], report),
    );

    handleChange({ sort, filters: nextFilters });
  }, [sort, handleChange]);

  const renderFilter = ({ filter, filterIndex }) => {
    const name = getReportFilterName(filter, availableFieldsByValue);

    const filterData = {
      ...filter,
      searchCriteriaIndex: filterIndex,
      withoutOrGroup: getWithoutOrGroup(filter),
    };

    return (
      <PageFilterItem
        name={name}
        titleText={name}
        key={filterIndex}
        filterData={filterData}
        useMenuPortalTarget={false}
        selectedReport={selectedReport}
        getItemListRequest={() => null}
        availableFields={availableFields}
        setUsedReport={handleSetUsedReport}
        settings={getFilterPopperSettings()}
      />
    );
  };

  return R.values(groupedFilters).map((filterGroup, groupIndex) => {
    const multipleFilters = R.gt(R.length(filterGroup), 1);

    if (R.not(multipleFilters)) return renderFilter(R.head(filterGroup));

    return (
      <Flex
        py='2px'
        key={groupIndex}
        borderRadius='5px'
        borderColor='grey'
        border='1px dashed'
      >
        {filterGroup.map((filterData, filterIndex) => (
          <Flex key={filterData.filterIndex}>
            {renderFilter(filterData)}
            {
              G.notEquals(filterIndex, R.dec(R.length(filterGroup))) &&
              <Box fontSize={11}>{G.getWindowLocale('titles:or', 'Or')}</Box>
            }
          </Flex>
        ))}
      </Flex>
    );
  });
};

const ReportFilters = ({
  sort = [],
  openModal,
  closeModal,
  reportType,
  filterProps,
  filters = [],
  handleChange,
  assessorials,
  geoFencingZones,
  disableRefFilter,
  excludedFieldNames,
  allAvailableRefTypesByScopeName,
  fleetDocumentAndCertificationTypes,
}) => {
  const availableFields = useMemo(() => getReportFiltersAvailableFields({
    reportType,
    filterProps,
    assessorials,
    geoFencingZones,
    disableRefFilter,
    allAvailableRefTypesByScopeName,
    fleetDocumentAndCertificationTypes,
  }), [
    reportType,
    filterProps,
    assessorials,
    geoFencingZones,
    disableRefFilter,
    allAvailableRefTypesByScopeName,
    fleetDocumentAndCertificationTypes,
  ]);

  const filterAvailableFields = useMemo(() => getReportFiltersAvailableFields({
    reportType,
    filterProps,
    assessorials,
    geoFencingZones,
    disableRefFilter,
    excludedFieldNames,
    allAvailableRefTypesByScopeName,
    fleetDocumentAndCertificationTypes,
  }), [
    reportType,
    filterProps,
    assessorials,
    geoFencingZones,
    disableRefFilter,
    excludedFieldNames,
    allAvailableRefTypesByScopeName,
    fleetDocumentAndCertificationTypes,
  ]);

  const handleOpenEditor = useCallback(() => {
    openModal({
      p: '0px',
      options: {
        width: 'auto',
        height: 'auto',
        maxWidth: '95vw',
        maxHeight: '95vh',
        title: G.getWindowLocale('titles:additional-filters-sort', 'Additional Filters/Sort'),
      },
      component: (
        <ReportFiltersEditor
          sortFields={sort}
          filterFields={filters}
          closeModal={closeModal}
          reportType={reportType}
          handleChange={handleChange}
          availableFields={availableFields}
          filterAvailableFields={filterAvailableFields}
        />
      ),
    });
  }, [
    sort,
    filters,
    openModal,
    closeModal,
    reportType,
    handleChange,
    availableFields,
    filterAvailableFields,
  ]);

  const handleChangeSort = useCallback(
    (nextSort) => handleChange({
      filters,
      sort: mapFieldsWithSequenceFromIndex(nextSort),
    }),
    [filters, handleChange],
  );

  const labelColor = G.getTheme('colors.dark.mainDark');

  return (
    <Box width='100%' pt={12}>
      <Flex alignItems='center'>
        <EditButton handleClick={handleOpenEditor} />
        <Box flex='1 1 auto'>
          <Flex minHeight={28} alignItems='center'>
            <Flex width={90} minWidth={90} fontSize={12} fontWeight={500} color={labelColor}>
              {`${G.getWindowLocale('titles:sort', 'Sort')}:`}
            </Flex>
            <Flex flexWrap='wrap'>
              {sort.map((sortField, fieldIndex) => (
                <ReportSortItem
                  sort={sort}
                  key={fieldIndex}
                  sortField={sortField}
                  fieldIndex={fieldIndex}
                  availableFields={availableFields}
                  handleChangeSort={handleChangeSort}
                />
              ))}
            </Flex>
          </Flex>
          <Flex mt={4} minHeight={28} alignItems='center'>
            <Flex width={90} minWidth={90} fontSize={12} fontWeight={500} color={labelColor}>
              {`${G.getWindowLocale('titles:filters', 'Filters')}:`}
            </Flex>
            <Flex flexWrap='wrap'>
              <ReportFilterItems
                sort={sort}
                filters={filters}
                reportType={reportType}
                handleChange={handleChange}
                availableFields={filterAvailableFields}
              />
            </Flex>
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
};

export default memo(ReportFilters);
